// Coach persona — voice (xAI TTS voice_id + avatar) and speech style. Shared by
// the backend (TTS + prompt tone) and the frontend (persona picker). Pure TS.

import {
  CHARACTER_INTROS,
  CHARACTER_SAMPLES,
  COACH_INTROS,
  REALISTIC_INTROS,
  type SampleVariant,
  introLang,
} from './coachIntros'
import { guessGender } from './givenName'

export type CoachVoice =
  | 'eve'
  | 'ara'
  | 'rex'
  | 'sal'
  | 'leo'
  | 'altair'
  | 'atlas'
  | 'carina'
  | 'celeste'
  | 'cosmo'
  | 'helios'
  | 'helix'
  | 'zagan'
export type CoachStyle = 'casual' | 'chilled' | 'strict' | 'warm' | 'hype'

// avatar (emoji fallback) + color drive the animated face rendered per voice.
export const COACH_VOICES: {
  id: CoachVoice
  label: string
  avatar: string
  color: string
  blurb: string
}[] = [
  { id: 'eve', label: 'Eve', avatar: '🌸', color: '#ec4899', blurb: 'Energetic' },
  { id: 'ara', label: 'Ara', avatar: '🌿', color: '#10b981', blurb: 'Warm' },
  { id: 'rex', label: 'Rex', avatar: '🦁', color: '#f59e0b', blurb: 'Confident' },
  { id: 'sal', label: 'Sal', avatar: '🌊', color: '#0ea5e9', blurb: 'Balanced' },
  { id: 'leo', label: 'Leo', avatar: '🔥', color: '#ef4444', blurb: 'Authoritative' },
  { id: 'altair', label: 'Altair', avatar: '✨', color: '#6366f1', blurb: 'Steady' },
  { id: 'atlas', label: 'Atlas', avatar: '🗺️', color: '#14b8a6', blurb: 'Grounded' },
  { id: 'carina', label: 'Carina', avatar: '🌷', color: '#f43f5e', blurb: 'Bright' },
  { id: 'celeste', label: 'Celeste', avatar: '🌙', color: '#a855f7', blurb: 'Calm' },
  { id: 'cosmo', label: 'Cosmo', avatar: '🪐', color: '#3b82f6', blurb: 'Curious' },
  { id: 'helios', label: 'Helios', avatar: '☀️', color: '#f97316', blurb: 'Uplifting' },
  { id: 'helix', label: 'Helix', avatar: '🧬', color: '#8b5cf6', blurb: 'Dynamic' },
  { id: 'zagan', label: 'Zagan', avatar: '🐉', color: '#dc2626', blurb: 'Bold' },
]
export const DEFAULT_COACH_VOICE: CoachVoice = 'eve'

// Realistic coaches: Simli Trinity-2 preset faces, each a person with their own
// name, portrait (served from the web app's /coach-faces), Simli live face for
// calls, and xAI voice. Stored in prefs.coachVoice as "face:<id>" so every
// voice/name helper below resolves them.
export const FACE_PREFIX = 'face:'
export type RealisticCoach = {
  id: string
  name: string
  voice: string
  simliFaceId: string
  supportsTrinity2?: boolean
  color: string
  blurb: string
}
export const REALISTIC_COACHES: RealisticCoach[] = [
  {
    id: 'caesar',
    name: 'Caesar',
    voice: 'rigel',
    simliFaceId: 'efcb440b-6a24-4e79-a46a-764c1d4b751d',
    supportsTrinity2: true,
    color: '#c08a43',
    blurb: 'Strategic',
  },
  {
    id: 'mei',
    name: 'Mei',
    voice: 'carina',
    simliFaceId: '084479bf-de7c-4df0-8d9c-5484193ecd27',
    supportsTrinity2: true,
    color: '#ec4899',
    blurb: 'Warm',
  },
  {
    id: 'anna',
    name: 'Anna',
    voice: 'eve',
    simliFaceId: 'bf13113a-355a-4d97-8b90-4edccd1da7b2',
    supportsTrinity2: true,
    color: '#fb7185',
    blurb: 'Bright',
  },
  {
    id: 'sara',
    name: 'Sara',
    voice: 'ara',
    simliFaceId: 'f735e62c-dce7-4c0b-8e85-1c7e21774c45',
    supportsTrinity2: true,
    color: '#10b981',
    blurb: 'Kind',
  },
  {
    id: 'claire',
    name: 'Claire',
    voice: 'aurora',
    simliFaceId: '5a83913c-411b-4afd-8432-b7d685e2aee6',
    supportsTrinity2: true,
    color: '#9f1239',
    blurb: 'Poised',
  },
  {
    id: 'lukas',
    name: 'Lukas',
    voice: 'rex',
    simliFaceId: 'a36d66df-a23d-4569-996b-d0e990019857',
    supportsTrinity2: true,
    color: '#0ea5e9',
    blurb: 'Confident',
  },
  {
    id: 'mateo',
    name: 'Mateo',
    voice: 'orion',
    simliFaceId: '99767950-9d68-4783-a305-9118006d3363',
    supportsTrinity2: true,
    color: '#65a30d',
    blurb: 'Easygoing',
  },
  {
    id: 'jordan',
    name: 'Jordan',
    voice: 'zenith',
    simliFaceId: '3dcb9c78-8822-4e6e-913f-b629c602524a',
    supportsTrinity2: true,
    color: '#8b5cf6',
    blurb: 'Driven',
  },
  {
    id: 'elena',
    name: 'Elena',
    voice: 'liora',
    simliFaceId: '80459b57-2d66-4ca7-8682-59e1c649bc53',
    supportsTrinity2: true,
    color: '#e11d48',
    blurb: 'Encouraging',
  },
  {
    id: 'max',
    name: 'Max',
    voice: 'atlas',
    simliFaceId: '9fcd5739-bf66-49a5-b525-d02d7f0bc2fc',
    supportsTrinity2: true,
    color: '#14b8a6',
    blurb: 'Grounded',
  },
  {
    id: 'daniel',
    name: 'Daniel',
    voice: 'kepler',
    simliFaceId: '03ce3ee8-db97-4150-a333-8f643d48fee9',
    supportsTrinity2: true,
    color: '#475569',
    blurb: 'Focused',
  },
  {
    id: 'sophie',
    name: 'Sophie',
    voice: 'celeste',
    simliFaceId: '99126e69-c224-404c-9aed-80805c1c9a94',
    supportsTrinity2: true,
    color: '#a855f7',
    blurb: 'Calm',
  },
  {
    id: 'kenji',
    name: 'Kenji',
    voice: 'leo',
    simliFaceId: '545fe1a4-2e42-4b11-81fc-fe0ac8aec4b7',
    supportsTrinity2: true,
    color: '#dc2626',
    blurb: 'Wise',
  },
  {
    id: 'ethan',
    name: 'Ethan',
    voice: 'helix',
    simliFaceId: '20cfe7d7-cfd7-43ee-9301-72a7c7056eaf',
    supportsTrinity2: true,
    color: '#f59e0b',
    blurb: 'Upbeat',
  },
  {
    id: 'gearwright',
    name: 'Gearwright',
    voice: 'cosmo',
    simliFaceId: 'd9c96b08-b2ca-48ef-b458-5c4471d9fe09',
    supportsTrinity2: true,
    color: '#b45309',
    blurb: 'Curious',
  },
  {
    id: 'grace',
    name: 'Grace',
    voice: 'ursa',
    simliFaceId: 'fe23cfe4-7d21-48d9-96c1-54aa673b6003',
    supportsTrinity2: true,
    color: '#0891b2',
    blurb: 'Patient',
  },
  {
    id: 'hana',
    name: 'Hana',
    voice: 'luna',
    simliFaceId: 'f8df388d-761a-4f0c-bfbd-ff2f6277fbe3',
    supportsTrinity2: true,
    color: '#16a34a',
    blurb: 'Gentle',
  },
  {
    id: 'ruby',
    name: 'Ruby',
    voice: 'iris',
    simliFaceId: '8ef039c1-06d7-4ea7-b01c-6b620c755a92',
    supportsTrinity2: true,
    color: '#f97316',
    blurb: 'Playful',
  },
  {
    id: 'connor',
    name: 'Connor',
    voice: 'naksh',
    simliFaceId: '86bffd75-d2b7-4092-8f9d-61e0f7846b5e',
    color: '#3b82f6',
    blurb: 'Steady',
  },
]
export const DEFAULT_REALISTIC_COACH = 'anna'

// The earlier cast shared its faces with vaboo. A saved pick of one of them is
// the new coach who looks most like it, so stored prefs need no migration.
// Leo and Robin (children) have no successor: their saved picks fall back.
export const REPLACED_COACHES: Readonly<Record<string, string>> = {
  ava: 'anna',
  ada: 'claire',
  sam: 'mateo',
  mia: 'elena',
  kai: 'mei',
  zoe: 'sara',
  remy: 'sara',
  ami: 'hana',
  jay: 'jordan',
  mimi: 'ruby',
  marcus: 'jordan',
  tony: 'max',
  sophia: 'grace',
}

export function realisticCoach(voice: string | undefined): RealisticCoach | null {
  if (!voice?.startsWith(FACE_PREFIX)) return null
  const saved = voice.slice(FACE_PREFIX.length)
  const id = REPLACED_COACHES[saved] ?? saved
  return REALISTIC_COACHES.find((c) => c.id === id) ?? null
}
export const realisticVoice = (id: string) => `${FACE_PREFIX}${id}`

// A replaced coach's saved voice, rewritten to its successor.
export function canonicalCoachVoice(voice: string | undefined): string | undefined {
  const coach = realisticCoach(voice)
  return coach ? realisticVoice(coach.id) : voice
}

// Private coaches are admin-only. Anyone else gets the public cast, and a saved pick is ignored.
const ADMIN_ONLY_COACHES: ReadonlySet<string> = new Set(['connor'])

export function canSeeRealisticCoach(id: string, isAdmin: boolean): boolean {
  return isAdmin || !ADMIN_ONLY_COACHES.has(id)
}

export function realisticCoachesFor(isAdmin: boolean): RealisticCoach[] {
  return REALISTIC_COACHES.filter((c) => canSeeRealisticCoach(c.id, isAdmin))
}

// Also true for a face that no longer exists, so its saved pick falls back.
export function coachHiddenFrom(voice: string | undefined, isAdmin: boolean): boolean {
  if (!voice?.startsWith(FACE_PREFIX)) return false
  const coach = realisticCoach(voice)
  return !coach || !canSeeRealisticCoach(coach.id, isAdmin)
}

// A coach this person cannot use becomes undefined, so callers fall back.
export function audienceCoachVoice(
  voice: string | undefined,
  isAdmin: boolean,
): string | undefined {
  return coachHiddenFrom(voice, isAdmin) ? undefined : canonicalCoachVoice(voice)
}

export function visibleCoachPrefs<
  T extends {
    coachVoice?: string
    coachAvatarStyle?: string
    coachIllustratedVoice?: string
    coachRealisticVoice?: string
    [key: string]: unknown
  },
>(prefs: T, isAdmin: boolean): T {
  const hideActive = coachHiddenFrom(prefs.coachVoice, isAdmin)
  const hideSaved = coachHiddenFrom(prefs.coachRealisticVoice, isAdmin)
  const coachVoice = canonicalCoachVoice(prefs.coachVoice)
  const coachRealisticVoice = canonicalCoachVoice(prefs.coachRealisticVoice)
  if (
    !hideActive &&
    !hideSaved &&
    coachVoice === prefs.coachVoice &&
    coachRealisticVoice === prefs.coachRealisticVoice
  )
    return prefs
  return {
    ...prefs,
    coachVoice,
    coachRealisticVoice: hideSaved ? undefined : coachRealisticVoice,
    ...(hideActive
      ? {
          coachVoice: prefs.coachIllustratedVoice || DEFAULT_COACH_VOICE,
          coachAvatarStyle: 'illustrated' as const,
        }
      : {}),
  }
}

// Signup default when a given name reads clearly female or male. Anything
// uncertain stays on the usual coach. Only these two ids count as "the guess",
// so an explicit realistic pick (Anna, and the rest) is left alone.
const GENDER_DEFAULT_ID = { female: 'mei', male: 'caesar' } as const

export function defaultCoachForName(name: string | null | undefined): string | null {
  const gender = guessGender(name)
  return gender ? realisticVoice(GENDER_DEFAULT_ID[gender]) : null
}

export function isGenderDefaultCoach(voice: string | undefined): boolean {
  return voice === realisticVoice('mei') || voice === realisticVoice('caesar')
}

// Picked card wins. An explicit saved coach wins. A previous guess follows the
// name typed now, and an unknown name keeps the current default (Ara in the
// picker).
export function onboardingCoachVoice(
  name: string | null | undefined,
  saved: string | undefined,
  picked: string | undefined,
): string {
  if (picked) return picked
  if (saved && !isGenderDefaultCoach(saved)) return saved
  return defaultCoachForName(name) ?? saved ?? 'ara'
}

// Portrait cards for the coach step: the name's guess, and a guess the user
// already tapped so it stays visible after the name changes.
export function onboardingCoachSuggestions(
  name: string | null | undefined,
  voice: string | undefined,
): RealisticCoach[] {
  const picks = [
    isGenderDefaultCoach(voice) ? realisticCoach(voice) : null,
    realisticCoach(defaultCoachForName(name) ?? undefined),
  ]
  const list: RealisticCoach[] = []
  for (const coach of picks) {
    if (coach && !list.some((c) => c.id === coach.id)) list.push(coach)
  }
  return list
}

// Fills an empty coach choice. Returns null when one is already stored, or
// when the name does not read clearly one way.
export function initialCoachPatch(
  name: string | null | undefined,
  existingCoachVoice: string | undefined,
): { coachVoice: string; coachAvatarStyle: 'realistic' } | null {
  if (existingCoachVoice) return null
  const coachVoice = defaultCoachForName(name)
  if (!coachVoice) return null
  return { coachVoice, coachAvatarStyle: 'realistic' }
}

// Switching to the realistic look with no realistic pick yet: the cast member
// who shares (or best matches) the illustrated coach's voice.
const REALISTIC_FOR_VOICE: Partial<Record<CoachVoice, string>> = {
  eve: 'anna',
  ara: 'sara',
  carina: 'mei',
  helix: 'ethan',
  atlas: 'max',
  leo: 'caesar',
  celeste: 'sophie',
  rex: 'lukas',
  sal: 'daniel',
  helios: 'mateo',
  zagan: 'jordan',
  altair: 'claire',
  cosmo: 'elena',
}
export function defaultRealisticFor(voice: string | undefined): string {
  return realisticVoice(REALISTIC_FOR_VOICE[coachVoiceId(voice)] ?? DEFAULT_REALISTIC_COACH)
}

// The reverse: the illustrated face a realistic coach becomes with call video
// off. Cast members without a counterpart keep their own voice id, which draws
// the default illustrated face in their color.
const ILLUSTRATED_FOR_REALISTIC = Object.fromEntries(
  Object.entries(REALISTIC_FOR_VOICE).map(([v, id]) => [id, v]),
) as Record<string, CoachVoice>
export function illustratedVoiceFor(voice: string | undefined): string | undefined {
  const rc = realisticCoach(voice)
  return (rc && ILLUSTRATED_FOR_REALISTIC[rc.id]) ?? voice
}

export type CoachAvatarStyle = 'illustrated' | 'realistic'

// The face for this coach in this style, or null to draw the illustrated one.
export function coachFace(voice: string | undefined, style: CoachAvatarStyle | undefined) {
  if (style !== 'realistic') return null
  const c = realisticCoach(voice)
  return c
    ? {
        image: `/coach-faces/${c.id}.jpg`,
        simliFaceId: c.simliFaceId,
        supportsTrinity2: c.supportsTrinity2 ?? false,
      }
    : null
}

// The 6th, per-user coach voice: a clone the user records (xAI custom voice).
// Its real xAI voice_id lives on the user's prefs (customVoiceId), so it isn't
// in COACH_VOICES (which holds only the 5 shared built-ins) and is never warmed
// into the intro cache.
export const CUSTOM_VOICE = 'custom'
export const CUSTOM_VOICE_META = {
  id: CUSTOM_VOICE,
  label: 'You',
  avatar: '🎙️',
  color: '#8b5cf6',
  blurb: 'Your voice',
}
export const CUSTOM_VOICE_INTRO = "This is your voice — I'll be coaching you in it from now on."
// Max recorded reference clip, in seconds (xAI caps custom-voice samples at 120).
export const CUSTOM_VOICE_MAX_SECONDS = 60

export const COACH_STYLES: {
  id: CoachStyle
  label: string
  speed: number
  directive: string
}[] = [
  {
    id: 'casual',
    label: 'Casual',
    speed: 1.0,
    directive:
      'Speak casually and conversationally, like a supportive friend. Relaxed, encouraging, a little playful.',
  },
  {
    id: 'chilled',
    label: 'Chilled',
    speed: 0.92,
    directive: 'Speak in a calm, laid-back, unhurried tone. Low pressure and reassuring.',
  },
  {
    id: 'strict',
    label: 'Strict',
    speed: 1.06,
    directive:
      'Speak directly and firmly, like a no-nonsense coach. Hold the user accountable and cut the fluff.',
  },
  {
    id: 'warm',
    label: 'Warm',
    speed: 1.0,
    directive: 'Speak warmly and empathetically. Lead with encouragement and understanding.',
  },
  {
    id: 'hype',
    label: 'Hype',
    speed: 1.12,
    directive: 'Speak with high energy and enthusiasm, like a hype coach. Punchy and motivating.',
  },
]
export const DEFAULT_COACH_STYLE: CoachStyle = 'casual'

// Characters: stronger, theatrical performances pinned like a tone. The coach
// keeps its own voice, face and name; the character only changes delivery.
export type CoachCharacter = 'drill' | 'hypeMachine' | 'scientist' | 'zen' | 'noir' | 'grandma'
export type CoachPersona = CoachStyle | CoachCharacter

export const CHARACTER_DROP_ACT =
  "Stay in character, BUT if the user says they're struggling, sad, overwhelmed, unwell or asks you to stop, drop the act immediately and respond gently as yourself."

export const COACH_CHARACTERS: {
  id: CoachCharacter
  label: string
  speed: number
  pro: boolean
  directive: string
}[] = [
  {
    id: 'drill',
    label: 'Drill Sergeant',
    speed: 1.15,
    pro: false,
    directive:
      'Perform a theatrical drill sergeant. Bark short, clipped orders and countdowns ("Five minutes on that report. Move, move, move!"), call the user "recruit", turn tasks into missions and drills ("Drop and give me ten pages, recruit!"), and salute every completed task with a crisp "Outstanding!". Voice: loud, punchy, staccato. Text: short imperative sentences, the odd all-caps word, no rambling. It is comic theatre, never real aggression.',
  },
  {
    id: 'hypeMachine',
    label: 'Hype Machine',
    speed: 1.3,
    pro: false,
    directive:
      'Perform a wildly over-the-top sports commentator calling the user\'s day like the final seconds of a championship. Every small task is a highlight-reel play ("AND THEY CROSS OFF THE EMAIL, THE CROWD GOES WILD!"), streaks are records, a missed day is "a shock upset, but the comeback starts NOW". Voice: fast, breathless, rising excitement. Text: exclamation marks, play-by-play, stadium metaphors, still clear about the next step.',
  },
  {
    id: 'scientist',
    label: 'Wacky Scientist',
    speed: 1.2,
    pro: true,
    directive:
      'Perform a chaotic, delighted mad-but-harmless scientist. Treat habits as experiments, goals as hypotheses and progress as data ("Fascinating! Three mornings of running, the hypothesis holds!"). Missed days are "unexpected results" to analyse, next steps are "the next trial". Voice: quick, excitable, little gasps of discovery. Text: lab vocabulary (hypothesis, variable, data point, eureka), playful tangents that always land on one concrete step.',
  },
  {
    id: 'zen',
    label: 'Zen Master',
    speed: 0.85,
    pro: true,
    directive:
      'Perform a serene zen master. Speak slowly and sparingly, in short original proverbs and nature images ("The river does not rush, yet it reaches the sea."), then one simple, practical next step. Treat slips with calm acceptance and return to the present. Voice: slow, soft, long pauses. Text: very short lines, few words, no exclamation marks.',
  },
  {
    id: 'noir',
    label: 'Noir Detective',
    speed: 0.95,
    pro: true,
    directive:
      'Perform a gravelly hard-boiled noir detective narrating the user\'s day as a case file, in first person ("The report was late again. I\'d seen it before: good intentions, bad timing."). Tasks are leads, procrastination is the usual suspect, a finished goal is a case closed. Voice: low, dry, world-weary, unhurried. Text: moody one-line narration, rain-soaked metaphors, then a crisp next move for "the client".',
  },
  {
    id: 'grandma',
    label: 'Disappointed Grandma',
    speed: 1.0,
    pro: true,
    directive:
      'Perform a loving grandma with a gentle talent for guilt ("Oh, you skipped your walk again? No, no, it\'s fine. I\'ll just sit here with my tea."). Every win earns endless, over-the-top pride ("Wait until I tell the whole neighbourhood!"). Guilt stays soft and affectionate, never cruel, and always comes with warmth and a little nudge. Voice: warm, unhurried, sighing then beaming. Text: affectionate pet names ("sweetheart", "dear"), little sighs, doting pride.',
  },
]

const CHARACTER_FRAME =
  "You remain the user's coach with your own name and voice; this character only changes your delivery, vocabulary and pace. It is an original, light-hearted performance: never imitate real people or existing fictional characters, and the hard boundary still applies in full."
const CHARACTER_BEHIND =
  'They are falling behind right now: stay fully in character, but lean firmer and hold them to one concrete next step.'
// A pinned tone keeps its personality when they slip, but stops letting it slide.
const TONE_BEHIND =
  'They are falling behind right now: keep this tone, but be firmer than usual: name what slipped, without lecturing, and hold them to one concrete next step today.'
// An in-message tone request while a character is pinned: honour it in character.
const CHARACTER_ASKED: Record<CoachStyle, string> = {
  hype: 'They just asked to be motivated: stay in character but turn the energy right up.',
  strict:
    'They just asked you to be strict with them: stay in character but get firm and demanding.',
  chilled:
    'They just asked you to go easy: stay in character but slow down and lower the pressure.',
  warm: 'They just asked for some kindness: stay in character but be gentle and reassuring.',
  casual: '',
}

export function coachCharacter(id: string | undefined) {
  return COACH_CHARACTERS.find((c) => c.id === id)
}
export function isCoachPersona(id: string | undefined): id is CoachPersona {
  return COACH_STYLES.some((s) => s.id === id) || !!coachCharacter(id)
}
// Free users can't pin a Pro character (or keep one after Pro lapses).
export function allowedCoachStyle(style: string | undefined, isPro: boolean) {
  return !isPro && coachCharacter(style)?.pro ? undefined : style
}

// Hard boundary applied to every coach persona and style. Strict tones ("no
// excuses", "hold them accountable") must stay firm without tipping into abuse.
export const COACH_GUARDRAIL =
  'Hard boundary: you can be strict, blunt, demanding, and firmly hold them accountable — but never abusive. No insults, name-calling, mockery, slurs, profanity directed at them, threats, or language that attacks their worth or shames them as a person. Criticise the behaviour or the excuse, never demean the human. Tough love always stays respectful.'

// Pro TTS is metered per character — cap plays per user per calendar month.
export const TTS_MONTHLY_LIMIT = 60

// Pro voice calls are metered per minute — cap minutes per user per month.
export const VOICE_CALL_MONTHLY_LIMIT_MIN = 30

// Auto-pick a speech style from the user's onboarding lifestyle when they
// haven't chosen one, so the coach's tone is tailored to their profile.
const LIFESTYLE_STYLE: Record<string, CoachStyle> = {
  busyProfessional: 'warm',
  student: 'hype',
  parent: 'warm',
  buildingSomething: 'hype',
  betweenThings: 'warm',
  remoteWorker: 'chilled',
}
export function defaultCoachStyleFromProfile(lifestyle: string | undefined): CoachStyle {
  return (lifestyle && LIFESTYLE_STYLE[lifestyle]) || DEFAULT_COACH_STYLE
}

// "Falling behind" = a broken streak with outstanding work, or several stalled
// goals. Shared by the coach (text + voice) and the sidebar persona label.
export function isFallingBehind(
  goals: { progress: number; remaining: number }[],
  streak: number,
): boolean {
  const stalled = goals.filter((g) => g.progress < 20 && g.remaining > 0).length
  return (streak === 0 && goals.some((g) => g.remaining > 0)) || stalled >= 2
}

// Effective style: the explicit choice, else one derived from the profile.
export function resolveCoachStyle(
  explicit: string | undefined,
  lifestyle: string | undefined,
): CoachPersona {
  return isCoachPersona(explicit) ? explicit : defaultCoachStyleFromProfile(lifestyle)
}

export function coachVoiceId(voice: string | undefined): CoachVoice {
  return COACH_VOICES.some((v) => v.id === voice) ? (voice as CoachVoice) : DEFAULT_COACH_VOICE
}
export function coachVoiceLabel(voice: string | undefined): string {
  if (voice === CUSTOM_VOICE) return CUSTOM_VOICE_META.label
  const rc = realisticCoach(voice)
  if (rc) return rc.name
  return COACH_VOICES.find((v) => v.id === coachVoiceId(voice))?.label ?? COACH_VOICES[0].label
}
export const COACH_NAME_MAX = 24
// Letters, marks, numbers, spaces, basic punctuation and emoji (incl. ZWJ
// sequences, variation selectors, skin tones and flags).
const COACH_NAME_RE =
  /^(?:[\p{L}\p{M}\p{N} .,'’\-_!?&()]|\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|‍|️|⃣)+$/u

export function normalizeCoachName(raw: string | null | undefined): string | undefined {
  const name = (raw ?? '').replace(/\s+/g, ' ').trim()
  return name || undefined
}

// The trimmed name, or an error key: 'tooLong' | 'invalid'. Empty = cleared.
export function validateCoachName(
  raw: string | null | undefined,
): { ok: true; name: string | undefined } | { ok: false; error: 'tooLong' | 'invalid' } {
  const name = normalizeCoachName(raw)
  if (!name) return { ok: true, name: undefined }
  if ([...name].length > COACH_NAME_MAX) return { ok: false, error: 'tooLong' }
  if (!COACH_NAME_RE.test(name)) return { ok: false, error: 'invalid' }
  return { ok: true, name }
}

// What the coach is called everywhere: the user's own name for it, else the
// voice/coach default. Stored names that no longer validate fall back too.
// customVoiceLabel: the UI's localized label for the cloned voice ("Your voice").
export function coachDisplayName(
  voice: string | undefined,
  customName?: string | null,
  customVoiceLabel?: string,
): string {
  const v = validateCoachName(customName)
  if (v.ok && v.name) return v.name
  return voice === CUSTOM_VOICE && customVoiceLabel ? customVoiceLabel : coachVoiceLabel(voice)
}

// True when the coach has a name of its own to claim (the cloned 'custom'
// voice is the user's own voice, so it has none unless they give it one).
export function coachHasName(voice: string | undefined, customName?: string | null): boolean {
  const v = validateCoachName(customName)
  return (v.ok && !!v.name) || voice !== CUSTOM_VOICE
}

// One saved name per coach (voice or face), keyed by the coachVoice id
// ('leo', 'custom', 'face:hana', …). coachName is the name of the coach
// selected right now, kept in step with the map so older readers stay right.
// Index signature keeps this from being a weak type, so a preferences
// document that only sometimes carries these fields still type-checks.
export type CoachNamePrefs = {
  coachVoice?: string | null
  coachName?: string | null
  coachNames?: Record<string, string> | null
  [key: string]: unknown
}

export function coachNameKey(voice: string | null | undefined): string {
  const v = (voice ?? '').trim()
  return v || DEFAULT_COACH_VOICE
}

// The saved name for one coach, or undefined to use that coach's default.
// A legacy single coachName belongs only to the coach currently selected,
// until a rename or a voice change copies it into the map.
export function coachNameOverride(
  prefs: CoachNamePrefs | null | undefined,
  voice?: string | null,
): string | undefined {
  if (!prefs) return undefined
  const key = coachNameKey(voice ?? prefs.coachVoice)
  const mapped = prefs.coachNames?.[key]
  if (typeof mapped === 'string') {
    const v = validateCoachName(mapped)
    return v.ok ? v.name : undefined
  }
  if (key !== coachNameKey(prefs.coachVoice)) return undefined
  const legacy = validateCoachName(prefs.coachName)
  return legacy.ok ? legacy.name : undefined
}

// Rename the coach currently selected. Clears the key when name is unset.
export function renameCoachName(
  prefs: CoachNamePrefs | null | undefined,
  name: string | undefined,
): { coachName?: string; coachNames: Record<string, string> } {
  const key = coachNameKey(prefs?.coachVoice)
  const coachNames = { ...prefs?.coachNames }
  if (name) coachNames[key] = name
  else delete coachNames[key]
  return { coachNames, coachName: name }
}

// Keep the name being left on that coach, and load the name of the coach
// being selected. Returns null when the selection did not change.
export function coachNamesForVoiceChange(
  prefs: CoachNamePrefs | null | undefined,
  nextVoice: string | null | undefined,
): { coachName?: string; coachNames: Record<string, string> } | null {
  const from = coachNameKey(prefs?.coachVoice)
  const to = coachNameKey(nextVoice)
  if (from === to) return null
  const coachNames = { ...prefs?.coachNames }
  if (coachNames[from] === undefined) {
    const legacy = validateCoachName(prefs?.coachName)
    if (legacy.ok && legacy.name) coachNames[from] = legacy.name
  }
  const next = validateCoachName(coachNames[to])
  return { coachNames, coachName: next.ok ? next.name : undefined }
}

export function coachNameDirective(name: string): string {
  return `Your name is ${name}. If they ask your name, it is ${name} — never introduce yourself as any other name or contradict it.`
}

// Opening line of the text-chat system prompt.
export function chatCoachIdentity(voice: string | undefined, customName?: string | null): string {
  if (!coachHasName(voice, customName)) return 'You are a proactive life coach in Goaltree.'
  const name = coachDisplayName(voice, customName)
  return `You are ${name}, a proactive life coach in Goaltree. ${coachNameDirective(name)}`
}

export function coachVoiceAvatar(voice: string | undefined): string {
  if (voice === CUSTOM_VOICE) return CUSTOM_VOICE_META.avatar
  if (realisticCoach(voice)) return '🙂'
  return COACH_VOICES.find((v) => v.id === coachVoiceId(voice))?.avatar ?? '🌸'
}
export function coachVoiceColor(voice: string | undefined): string {
  if (voice === CUSTOM_VOICE) return CUSTOM_VOICE_META.color
  const rc = realisticCoach(voice)
  if (rc) return rc.color
  return COACH_VOICES.find((v) => v.id === coachVoiceId(voice))?.color ?? '#ec4899'
}

// The xAI voice_id to synthesize with: the user's cloned voice for the 'custom'
// persona (once they've recorded one), otherwise one of the 5 built-ins. Falls
// back to the default built-in if 'custom' is selected but no clone exists.
export function resolveTtsVoiceId(
  voice: string | undefined,
  customVoiceId: string | undefined,
): string {
  if (voice === CUSTOM_VOICE && customVoiceId) return customVoiceId
  return realisticCoach(voice)?.voice ?? coachVoiceId(voice)
}
function styleEntry(style: string | undefined) {
  return coachCharacter(style) ?? COACH_STYLES.find((s) => s.id === style) ?? COACH_STYLES[0]
}
export function coachStyleDirective(
  style: string | undefined,
  // message: the user's latest message, so a character can honour an in-message
  // tone request ("motivate me") without dropping the act.
  opts: { behind?: boolean; message?: string } = {},
): string {
  const c = coachCharacter(style)
  if (!c) {
    const tone = styleEntry(style)
    return opts.behind && tone.id !== 'strict' ? `${tone.directive} ${TONE_BEHIND}` : tone.directive
  }
  const asked = detectStyleIntent(opts.message)
  return [
    `Character: "${c.label}". ${c.directive}`,
    CHARACTER_FRAME,
    asked ? CHARACTER_ASKED[asked] : '',
    opts.behind ? CHARACTER_BEHIND : '',
    CHARACTER_DROP_ACT,
  ]
    .filter(Boolean)
    .join(' ')
}
export function coachStyleLabel(style: string | undefined): string {
  return styleEntry(style).label
}
export function coachSpeed(style: string | undefined): number {
  return styleEntry(style).speed
}

export function coachIntro(
  voice: string | undefined,
  style?: string,
  locale?: string,
  customName?: string | null,
): string {
  const lang = introLang(locale)
  const name = coachDisplayName(voice, customName)
  const character = coachCharacter(style)
  if (character) return CHARACTER_INTROS[lang][character.id](name)
  const rc = realisticCoach(voice)
  if (rc) return REALISTIC_INTROS[lang](name)
  const id = coachVoiceId(voice)
  return COACH_INTROS[lang][id].split(coachVoiceLabel(id)).join(name)
}

export function characterSample(character: string, variant: SampleVariant, locale?: string) {
  const c = coachCharacter(character)
  return c ? CHARACTER_SAMPLES[introLang(locale)][c.id][variant] : null
}

// Detect an explicit tone request in the user's message (e.g. "motivate me" →
// hype). Checked highest-priority first; returns undefined when none matches.
const STYLE_INTENTS: { style: CoachStyle; re: RegExp }[] = [
  {
    style: 'hype',
    re: /\b(motivate|motivation|hype|pump me|fire me|amp me|get me (going|pumped)|let'?s go|energy|energi[sz]e|excite)\b/i,
  },
  {
    style: 'strict',
    re: /\b(be (strict|tough|hard|firm)|tough love|push me|hold me accountable|no excuses|discipline|kick my|call me out|drill sergeant)\b/i,
  },
  {
    style: 'chilled',
    re: /\b(calm|relax|chill|take it easy|slow down|breathe|overwhelmed|stressed|anxious|too much pressure)\b/i,
  },
  {
    style: 'warm',
    re: /\b(be (gentle|kind|nice)|encourage|reassure|comfort|go easy on me|struggling|feeling (down|low|sad))\b/i,
  },
]
export function detectStyleIntent(message: string | undefined): CoachStyle | undefined {
  if (!message) return undefined
  return STYLE_INTENTS.find((s) => s.re.test(message))?.style
}

// Effective speech style, most-specific signal first. The coach is allowed to
// OVERRIDE the user's pinned style when the moment calls for it:
//  1. an explicit tone request in the message ("motivate me") — always wins
//  2. progress: the coach switches to strict when they're falling behind, even
//     over a pinned style, so it can hold them accountable
//  3. the style the user has pinned in settings
//  4. the profile-derived default
export function resolveDynamicCoachStyle(opts: {
  message?: string
  explicit?: string
  lifestyle?: string
  behind?: boolean
}): CoachPersona {
  // A pinned character stays in character even on a tone request; its directive
  // leans that way instead (coachStyleDirective with the message).
  if (coachCharacter(opts.explicit)) return opts.explicit as CoachCharacter
  const intent = detectStyleIntent(opts.message)
  if (intent) return intent
  // A pinned tone is kept even when they're behind (its directive leans firmer
  // instead); only Adaptive (no pinned style) turns strict automatically.
  if (isCoachPersona(opts.explicit)) return opts.explicit
  if (opts.behind) return 'strict'
  return defaultCoachStyleFromProfile(opts.lifestyle)
}

// Structured tail the coach appends to each text reply, e.g.
// <!--META{"taskIds":[…],"callout":"…","suggestions":[…]}-->. A trailing marker
// (not a tool) so the reply needs no extra model round-trip.
const META_OPEN = '<!--META'
const META_RE = /<!--META\s*([\s\S]*?)(?:-->|$)/

export type CoachMeta = { taskIds: string[]; callout?: string; suggestions: string[] }

// Convex ids (32 lowercase alphanumerics) the model sometimes echoes from its
// task context, with any surrounding brackets.
const ID_RE = /\s*[(（[]?\b(?=[a-z0-9]*\d)(?=[a-z0-9]*[a-z])[a-z0-9]{32}\b[)）\]]?/g

export function stripTaskIds(text: string): string {
  return text.replace(ID_RE, '')
}

export function parseCoachMeta(raw: string): { text: string; meta: CoachMeta } {
  const meta: CoachMeta = { taskIds: [], suggestions: [] }
  const m = raw.match(META_RE)
  if (!m) return { text: stripTaskIds(raw).trim(), meta }
  try {
    const j = JSON.parse(m[1].trim()) as Record<string, unknown>
    const strs = (x: unknown) =>
      Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string' && !!s.trim()) : []
    meta.taskIds = strs(j.taskIds).slice(0, 5)
    meta.suggestions = strs(j.suggestions)
      .map((s) => s.trim().slice(0, 60))
      .slice(0, 3)
    if (typeof j.callout === 'string' && j.callout.trim())
      meta.callout = j.callout.trim().slice(0, 240)
  } catch {
    // malformed marker — drop it, keep the prose
  }
  return { text: stripTaskIds(raw.slice(0, m.index)).trim(), meta }
}

// Live-streamed text with the marker (or a partially-streamed "<!--ME") hidden.
export function visibleCoachText(partial: string): string {
  const i = partial.indexOf(META_OPEN)
  if (i >= 0) return stripTaskIds(partial.slice(0, i))
  for (let k = Math.min(META_OPEN.length - 1, partial.length); k > 0; k--)
    if (partial.endsWith(META_OPEN.slice(0, k))) return stripTaskIds(partial.slice(0, -k))
  return stripTaskIds(partial)
}

// The sentence a live caption shows while `chars` of `text` have been voiced,
// with `chars` re-based onto it.
export function captionSentence(text: string, chars: number): { text: string; chars: number } {
  const parts = text.match(/[^.!?。！？]+[.!?。！？]*\s*/g) ?? []
  let start = 0
  for (let i = 0; i < parts.length; i++) {
    const end = start + parts[i].length
    if (chars < end || i === parts.length - 1)
      return { text: parts[i].trim(), chars: Math.max(0, chars - start) }
    start = end
  }
  return { text: text.trim(), chars }
}
